import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Section, ActionLink } from "@/components/luminous/interior";
import { Reveal, routesFor, type Lang } from "@/components/luminous/primitives";
import { publicCaseStudies, type CaseStudy } from "@/data/caseStudiesData";
import { getPublicCaseTitle } from "@/data/clientDisclosure";

interface CaseStudiesGridProps {
  language: Lang;
}

const t = {
  es: {
    label: "Casos documentados",
    title: "Un proyecto en detalle, y el resto del recorrido",
    intro:
      "El caso principal muestra el contexto completo: industria, problema y trabajo realizado. Los demás casos públicos siguen abajo, con la misma información resumida.",
    featuredKicker: "Caso principal",
    problem: "Problema.",
    work: "Trabajo realizado.",
    viewCase: "Ver caso completo",
    secondaryLabel: "Más casos públicos",
    empty:
      "Los demás casos están en proceso de verificación y autorización del cliente. Mientras tanto, la metodología de trabajo describe cómo se abordan estos proyectos.",
  },
  en: {
    label: "Documented cases",
    title: "One project in depth, and the rest of the record",
    intro:
      "The main case shows the full context: industry, problem and work performed. The other public cases follow below, with the same information summarized.",
    featuredKicker: "Main case",
    problem: "Problem.",
    work: "Work performed.",
    viewCase: "View full case",
    secondaryLabel: "More public cases",
    empty:
      "The other cases are pending client verification and authorization. In the meantime, the published methodology describes how these projects are approached.",
  },
} as const;

const caseHref = (routes: ReturnType<typeof routesFor>, slug: string) =>
  `${routes.caseStudies}/${slug}`;

const SecondaryCase = ({
  cs,
  language,
  index,
}: {
  cs: CaseStudy;
  language: Lang;
  index: number;
}) => {
  const routes = routesFor(language);
  const industry = language === "es" ? cs.industry : cs.industryEn;
  const services = language === "es" ? cs.services : cs.servicesEn;
  const situation = language === "es" ? cs.situation : cs.situationEn;
  const step = cs.steps[0];
  const stepText = step ? (language === "es" ? step.description : step.descriptionEn) : null;

  return (
    <Reveal
      as="article"
      className="border-t border-[color:var(--v3-line)] py-10 first:border-t-0 first:pt-0"
    >
      <div className="grid gap-6 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-8">
          <p className="v3-micro">
            <span style={{ color: "var(--v3-blue)" }}>{String(index + 1).padStart(2, "0")}</span>
            <span className="mx-2" aria-hidden>
              ·
            </span>
            {industry}
            <span className="mx-2" aria-hidden>
              ·
            </span>
            {cs.location}
          </p>
          <h3
            className="v3-display mt-3"
            style={{ fontSize: "clamp(1.3rem, 1.9vw, 1.7rem)" }}
          >
            {getPublicCaseTitle(cs.slug, cs.title, language)}
          </h3>
          <p className="v3-body mt-4">{situation}</p>
          {stepText ? (
            <p className="v3-note mt-3">
              <strong className="font-semibold text-[color:var(--v3-ink)]">
                {t[language].work}
              </strong>{" "}
              {stepText}
            </p>
          ) : null}
          <div className="mt-5 flex flex-wrap gap-x-4 gap-y-1">
            {services.slice(0, 4).map((s) => (
              <span key={s} className="v3-note">
                {s}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-end lg:col-span-4 lg:justify-end">
          <Link
            to={caseHref(routes, cs.slug)}
            className="v3-inline-link"
          >
            {t[language].viewCase}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>
    </Reveal>
  );
};

export const CaseStudiesGrid = ({ language }: CaseStudiesGridProps) => {
  const c = t[language];
  const routes = routesFor(language);
  const [featured, ...rest] = publicCaseStudies;

  if (!featured) {
    return (
      <Section id="casos" index="01" label={c.label} title={c.title} tone="white">
        <p className="v3-body">{c.empty}</p>
      </Section>
    );
  }

  const industry = language === "es" ? featured.industry : featured.industryEn;
  const services = language === "es" ? featured.services : featured.servicesEn;
  const situation = language === "es" ? featured.situation : featured.situationEn;
  const problems = language === "es" ? featured.problems : featured.problemsEn;
  const steps = featured.steps.map((s) => (language === "es" ? s.description : s.descriptionEn));

  return (
    <Section id="casos" index="01" label={c.label} title={c.title} intro={c.intro} tone="white">
      {/* Caso destacado: composición amplia */}
      <article className="grid gap-10 border-t border-[color:var(--v3-line)] pt-10 lg:grid-cols-12 lg:gap-14">
        <Reveal className="lg:col-span-7">
          <p className="v3-micro">
            <span style={{ color: "var(--v3-blue)" }}>{c.featuredKicker}</span> · {featured.location}
          </p>
          <h3 className="v3-display mt-4" style={{ fontSize: "clamp(1.6rem, 2.6vw, 2.2rem)" }}>
            {getPublicCaseTitle(featured.slug, featured.title, language)}
          </h3>
          <p className="v3-note mt-3">{industry}</p>
          <p className="v3-body mt-5">{situation}</p>

          <p className="v3-body mt-5">
            <strong>{c.problem}</strong> {problems[0]}
          </p>
          {steps[0] ? (
            <p className="v3-body mt-3">
              <strong>{c.work}</strong> {steps[0]}
            </p>
          ) : null}

          <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 border-t border-[color:var(--v3-line)] pt-5">
            {services.map((s) => (
              <span key={s} className="v3-note">
                {s}
              </span>
            ))}
          </div>

          <ActionLink to={caseHref(routes, featured.slug)}>
            {c.viewCase}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </ActionLink>
        </Reveal>

        <div className="lg:col-span-5">
          <dl className="border-t border-[color:var(--v3-line)] lg:border-t-0 lg:border-l lg:pl-10" style={{ borderColor: "var(--v3-line)" }}>
            {problems.slice(0, 4).map((p, i) => (
              <div key={p} className="border-b border-[color:var(--v3-line)] py-3">
                <dt className="v3-micro" style={{ color: "var(--v3-blue)" }}>
                  {String(i + 1).padStart(2, "0")}
                </dt>
                <dd className="mt-1 text-[0.95rem] leading-relaxed text-[color:var(--v3-ink-soft)]">
                  {p}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </article>

      {/* Índice secundario del resto de casos públicos */}
      {rest.length > 0 ? (
        <div className="mt-14 border-t-2 border-[color:var(--v3-line)] pt-2">
          <p className="v3-micro mt-6">{c.secondaryLabel}</p>
          <div className="mt-2">
            {rest.map((cs, i) => (
              <SecondaryCase key={cs.id} cs={cs} language={language} index={i} />
            ))}
          </div>
        </div>
      ) : null}
    </Section>
  );
};
